import type { InboxItem, InboxResponse } from "./types";

export const INBOX_PAGE_SIZE = 50;
export const INBOX_MAX_PAGE_SIZE = 200;
export const INBOX_CACHE_TTL_MS = 60_000;
export const INBOX_CACHE_MAX_SCOPES = 8;

export type InboxFeedSnapshot = {
  items: InboxItem[];
  total: number;
  nextCursor: string | null;
  hasMore: boolean;
  feedVersion: string;
  stale: boolean;
  fetchedAt: number;
  scrollTop: number;
  /** Exact client-side partition of a complete All-channel snapshot. */
  derivedFromCompleteScope?: boolean;
};

export type InboxFeedScope = {
  organizationId?: string | null;
  workspaceId?: string | null;
  filter: string;
  channel?: string | null;
  search?: string | null;
  campaignId?: string | null;
  jobId?: string | null;
};

export function inboxFeedCacheKey(scope: InboxFeedScope): string {
  return JSON.stringify([
    scope.organizationId || "",
    scope.workspaceId || "",
    scope.filter,
    scope.channel || "",
    scope.search?.trim().toLowerCase() || "",
    scope.campaignId || "",
    scope.jobId || "",
  ]);
}

export function getInboxSnapshotForScope(
  cache: Map<string, InboxFeedSnapshot>,
  scope: InboxFeedScope,
): InboxFeedSnapshot | undefined {
  const exact = cache.get(inboxFeedCacheKey(scope));
  if (!scope.channel) return exact;

  const allChannels = cache.get(
    inboxFeedCacheKey({ ...scope, channel: null }),
  );
  const completeAll =
    allChannels &&
    !allChannels.stale &&
    !allChannels.hasMore &&
    !allChannels.nextCursor &&
    allChannels.items.length === allChannels.total
      ? allChannels
      : undefined;
  if (
    exact &&
    !exact.stale &&
    (!completeAll || exact.fetchedAt >= completeAll.fetchedAt)
  ) {
    return exact;
  }
  if (!completeAll) return exact;

  const items = completeAll.items.filter((item) =>
    item.channels.some((channel) => channel === scope.channel),
  );
  return {
    ...completeAll,
    items,
    total: items.length,
    nextCursor: null,
    hasMore: false,
    scrollTop: 0,
    derivedFromCompleteScope: true,
  };
}

function uniqueItems(groups: InboxItem[][]): InboxItem[] {
  const seen = new Set<string>();
  const items: InboxItem[] = [];
  for (const group of groups) {
    for (const item of group) {
      if (seen.has(item._id)) continue;
      seen.add(item._id);
      items.push(item);
    }
  }
  return items;
}

export function inboxSnapshotFromPage(
  response: InboxResponse,
  {
    now = Date.now(),
    scrollTop = 0,
  }: { now?: number; scrollTop?: number } = {},
): InboxFeedSnapshot {
  return {
    items: uniqueItems([response.items || []]),
    total: Math.max(0, response.total || 0),
    nextCursor: response.nextCursor || null,
    hasMore: Boolean(response.hasMore && response.nextCursor),
    feedVersion: response.feedVersion,
    stale: false,
    fetchedAt: now,
    scrollTop,
  };
}

export function appendInboxPage(
  current: InboxFeedSnapshot,
  response: InboxResponse,
  now = Date.now(),
): InboxFeedSnapshot {
  if (response.feedVersion !== current.feedVersion) {
    return { ...current, stale: true };
  }

  return {
    ...current,
    items: uniqueItems([current.items, response.items || []]),
    total: Math.max(0, response.total || 0),
    nextCursor: response.nextCursor || null,
    hasMore: Boolean(response.hasMore && response.nextCursor),
    fetchedAt: now,
  };
}

type InboxPageFetcher = (
  cursor?: string,
  limit?: number,
) => Promise<InboxResponse>;

type RebuildInboxSnapshotOptions = {
  now?: number;
  scrollTop?: number;
  maxAttempts?: number;
  shouldRetry?: (error: unknown) => boolean;
};

class InboxFeedChangedDuringRebuildError extends Error {
  constructor() {
    super("Inbox changed while rebuilding the loaded window");
    this.name = "InboxFeedChangedDuringRebuildError";
  }
}

/**
 * Rebuild a cursor window after a mutation changes the feed generation. This
 * keeps every page in one generation while restoring the depth the recruiter
 * had already loaded instead of replacing it with the first 50 rows.
 */
export async function rebuildInboxSnapshotToDepth(
  fetchPage: InboxPageFetcher,
  targetCount: number,
  {
    now = Date.now(),
    scrollTop = 0,
    maxAttempts = 2,
    shouldRetry,
  }: RebuildInboxSnapshotOptions = {},
): Promise<InboxFeedSnapshot> {
  const requestedDepth = Math.max(
    INBOX_PAGE_SIZE,
    Math.floor(Number.isFinite(targetCount) ? targetCount : INBOX_PAGE_SIZE),
  );
  const attempts = Math.max(1, Math.floor(maxAttempts));
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      let snapshot = inboxSnapshotFromPage(
        await fetchPage(
          undefined,
          Math.min(requestedDepth, INBOX_MAX_PAGE_SIZE),
        ),
        { now, scrollTop },
      );
      const seenCursors = new Set<string>();

      while (
        snapshot.items.length < Math.min(requestedDepth, snapshot.total) &&
        snapshot.hasMore &&
        snapshot.nextCursor
      ) {
        const cursor = snapshot.nextCursor;
        if (seenCursors.has(cursor)) {
          throw new Error("Inbox pagination returned the same cursor twice");
        }
        seenCursors.add(cursor);
        const remaining = Math.max(1, requestedDepth - snapshot.items.length);
        const next = appendInboxPage(
          snapshot,
          await fetchPage(cursor, Math.min(remaining, INBOX_MAX_PAGE_SIZE)),
          now,
        );
        if (next.stale) throw new InboxFeedChangedDuringRebuildError();
        snapshot = next;
      }

      return snapshot;
    } catch (error) {
      lastError = error;
      const retryable =
        error instanceof InboxFeedChangedDuringRebuildError ||
        Boolean(shouldRetry?.(error));
      if (!retryable || attempt + 1 >= attempts) throw error;
    }
  }

  throw lastError;
}

/**
 * Polling refreshes only the head page. Keep already loaded tail rows when the
 * scope is still growing/stable, but reset to the authoritative head if rows
 * left the filter (total shrank) or the whole result now fits in one page.
 */
export function refreshInboxHead(
  current: InboxFeedSnapshot,
  response: InboxResponse,
  now = Date.now(),
): InboxFeedSnapshot {
  const nextHead = inboxSnapshotFromPage(response, {
    now,
    scrollTop: current.scrollTop,
  });
  if (
    response.feedVersion !== current.feedVersion &&
    current.items.length > (response.limit || INBOX_PAGE_SIZE)
  ) {
    // Never merge cursor pages from two feed generations. Preserve the
    // reader's current window and require one authoritative refresh.
    return { ...current, stale: true };
  }
  const shouldReset = response.total < current.total || !response.hasMore;
  if (
    shouldReset ||
    current.items.length <= (response.limit || INBOX_PAGE_SIZE)
  )
    return nextHead;

  const items = uniqueItems([nextHead.items, current.items]).slice(
    0,
    Math.max(response.total, nextHead.items.length),
  );
  // A previously complete cache has no tail cursor. If the scope grew beyond
  // what the new head + cached rows can account for, restart at the new head so
  // the following page uses a valid continuation and retains server ordering.
  if (!current.nextCursor && items.length < response.total) return nextHead;

  const fullyLoaded = items.length >= response.total;
  return {
    ...current,
    items,
    total: nextHead.total,
    // The existing tail cursor remains the correct continuation after the
    // last cached row; replacing it with the head cursor would re-fetch page 2.
    nextCursor: fullyLoaded ? null : current.nextCursor,
    hasMore: fullyLoaded ? false : current.hasMore,
    fetchedAt: now,
  };
}

export function isFreshInboxSnapshot(
  snapshot: InboxFeedSnapshot | undefined,
  now = Date.now(),
  ttlMs = INBOX_CACHE_TTL_MS,
): snapshot is InboxFeedSnapshot {
  return Boolean(
    snapshot && !snapshot.stale && now - snapshot.fetchedAt <= ttlMs,
  );
}

export function putInboxSnapshot(
  cache: Map<string, InboxFeedSnapshot>,
  key: string,
  snapshot: InboxFeedSnapshot,
  maxScopes = INBOX_CACHE_MAX_SCOPES,
): void {
  cache.delete(key);
  cache.set(key, snapshot);
  while (cache.size > maxScopes) {
    const oldestKey = cache.keys().next().value as string | undefined;
    if (!oldestKey) break;
    cache.delete(oldestKey);
  }
}
